import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography } from "@material-tailwind/react";
import { useAuth } from '../../AuthContext';
import SearchableSelect from '../../../common/SearchableSelect';

let _weeksCache = null;

const normalizeWeeks = (weeksArray) => {
    const map = new Map();
    for (const w of weeksArray) {
        const key = (w.week || '').trim();
        if (!key) continue;
        if (!map.has(key)) {
            map.set(key, { ...w, week: key });
        }
    }
    return Array.from(map.values());
};

const WeekSelector = ({ selectedWeek, onWeekChange, className = "" }) => {
    const { rootUrl } = require('../../../../config/config');
    const { token } = useAuth();
    const [weeks, setWeeks] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchWeeks = async () => {
            try {
                // Use cached weeks if available to avoid duplicate network calls
                if (_weeksCache) {
                    setWeeks(_weeksCache);
                    // Auto-select most recent week if none selected
                    if (!selectedWeek && _weeksCache.length > 0) {
                        onWeekChange(_weeksCache[0].week);
                    }
                    return;
                }

                const response = await axios.get(`${rootUrl}/weekly-summary/weeks`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                // Normalize and deduplicate week keys returned by API
                const normalized = normalizeWeeks(response.data || []);
                _weeksCache = normalized;
                setWeeks(normalized);

                // Auto-select most recent week if none selected
                if (!selectedWeek && normalized.length > 0) {
                    onWeekChange(normalized[0].week);
                }
            } catch (error) {
                console.error('Error fetching weeks:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchWeeks();
        // Intentionally only run once on mount or when rootUrl/token change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rootUrl, token]);

    const formatWeekDisplay = (week) => {
        const weekData = weeks.find(w => w.week === week);
        if (weekData) {
            const startDate = new Date(weekData.start_date).toLocaleDateString();
            const endDate = new Date(weekData.end_date).toLocaleDateString();
            return `Week ${week} (${startDate} - ${endDate})`;
        }
        return `Week ${week}`;
    };

    const getSelectedWeekDateRange = () => {
        if (!selectedWeek) return null;
        const weekData = weeks.find(w => w.week === selectedWeek);
        if (weekData) {
            const startDate = new Date(weekData.start_date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
            const endDate = new Date(weekData.end_date).toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
            return `${startDate} - ${endDate}`;
        }
        return null;
    };

    if (loading) {
        return (
            <div className={`space-y-2 ${className}`}>
                <div className="animate-pulse h-10 rounded-lg"></div>
            </div>
        );
    }

    return (
        <div className={`space-y-2 ${className}`}>
            <SearchableSelect
                placeholder="Choose Week"
                value={selectedWeek}
                onChange={(value) => onWeekChange(value)}
                loading={loading}
                options={weeks.map(week => ({
                    value: week.week,
                    label: formatWeekDisplay(week.week)
                }))}
            />
            {selectedWeek && getSelectedWeekDateRange() && (
                <Typography variant="small" color="gray" className="mt-1">
                    {getSelectedWeekDateRange()}
                </Typography>
            )}
        </div>
    );
};

export default WeekSelector;
